// @ts-nocheck

/**
 * @typedef {Object} listConstraints
 * @property {String} top
 * @property {String} left
 * @property {String} bottom
 * @property {String} minWidth
 * @property {String} maxHeight
 */

/**
 * @callback optionsEventCallback
 * @param {option|option[]} value
 */

/**
 * @typedef {Object} option
 * @property {String} value
 * @property {String} label
 * @property {Number} [idx]
 * @property {Boolean} [disabled]
 */

import "./Options";

const CLASSES = {
    base: "combobox",
    open: "combobox-open",
    list: "combobox-list",
    activeInput: "combobox-active",
    item: "combobox-item",
    selected: "combobox-list-selected"
};

const KEYCODES = {
    up: "ArrowUp", // win, Mac, Linux
    down: "ArrowDown", // win, Mac, Linux
    enter: "Enter", // win, Mac, Linux
    backspace: "Backspace", // win, Mac, Linux
    escape: "Escape", // win, Mac, Linux
    tab: "Tab" // win, Mac, Linux
};

export { CLASSES };

/**
 * Vanilla JS for maximum performance. Renders an array of 20000 words in ~60ms
 */
export class OptionsHandler {
    static listMaxHeight;

    constructor(boundingBox, input) {
        this.boundingBox = boundingBox;
        this.searchInput = input;
        this.buffer = {
            /**
             * @type {Array.<option>}
             */
            items: null,
            /**
             * @type {Array.<option>}
             */
            filteredItems: null,
            selectedIndex: 0,
            itemIndex: 0,
            selectedOptions: []
        };
        this.createOption = this.createOption.bind(this);
        this.searchable = false;
        this.multiselect = false;
        this.initElements().initListener().addListeners();
        this.resetPosition();
    }

    /**
     * initialize list element
     * @returns {OptionsHandler}
     */
    initElements() {
        this.list = document.createElement("combobox-list");
        this.list.searchInput = this.searchInput;
        if (!OptionsHandler.listMaxHeight) {
            OptionsHandler.listMaxHeight = parseInt(
                getComputedStyle(document.querySelector(":root")).getPropertyValue("--x-dropdown-list-max-height")
            );
        }
        return this;
    }

    /**
     * initialize handlers
     * @returns {OptionsHandler}
     */
    initListener() {
        this.handleFocus = this.focusHandler.bind(this);
        this.handleHide = this.hide.bind(this);
        this.handleListPointerDown = this.listPointerDownHandler.bind(this);
        this.handleListPointerUp = this.listPointerUpHandler.bind(this);
        this.handleListScroll = this.listScrollHandler.bind(this);
        this.handleKeyup = this.keyupHandler.bind(this);
        this.handleKeydown = this.keydownHandler.bind(this);
        this.handleInput = this.inputHandler.bind(this);
        this.handleReposition = this.repositionHandler.bind(this);
        this.handleBlur = this.blurHandler.bind(this);
        return this;
    }

    /**
     * add event listeners
     * @returns {OptionsHandler}
     */
    addListeners() {
        this.list.addEventListener("pointerdown", this.handleListPointerDown);
        this.list.addEventListener("pointerup", this.handleListPointerUp, { capture: true });
        this.list.addScrollListener(this.handleListScroll);
        this.searchInput.addEventListener("focus", this.handleFocus);
        this.searchInput.addEventListener("blur", this.handleBlur);
        this.searchInput.addEventListener("keyup", this.handleKeyup);
        this.searchInput.addEventListener("keydown", this.handleKeydown);
        this.searchInput.addEventListener("input", this.handleInput);
        window.addEventListener("resize", this.handleReposition);
        return this;
    }

    /**
     * remove event listeners
     * @returns {OptionsHandler}
     */
    removeListeners() {
        this.list.removeEventListener("pointerdown", this.handleListPointerDown);
        this.list.removeEventListener("pointerup", this.handleListPointerUp);
        this.searchInput.removeEventListener("focus", this.handleFocus);
        this.searchInput.removeEventListener("blur", this.handleBlur);
        this.searchInput.removeEventListener("keyup", this.handleKeyup);
        this.searchInput.removeEventListener("keydown", this.handleKeydown);
        this.searchInput.removeEventListener("input", this.handleInput);
        window.removeEventListener("resize", this.handleReposition);
        return this;
    }

    /**
     * attach change listener
     * @param {optionsEventCallback} callback
     */
    onChange(callback) {
        this.cbChange = callback;
    }

    /**
     * attach blur listener
     * @param {optionsEventCallback} callback
     */
    onBlur(callback) {
        this.cbBlur = callback;
    }

    /**
     * attach focus listener
     * @param {optionsEventCallback} callback
     */
    onFocus(callback) {
        this.cbFocus = callback;
    }

    /**
     * attach open listener
     * @param {optionsEventCallback} callback
     */
    onOpen(callback) {
        this.cbOpen = callback;
    }

    /**
     * attach close listener
     * @param {optionsEventCallback} callback
     */
    onClose(callback) {
        this.cbClose = callback;
    }

    /**
     * call a callback by type
     * @param {String} type
     */
    callCallback(type) {
        const cb = this[`cb${type}`];
        if ("function" === typeof cb) {
            const args = this.multiselect ? this.selectedOptions : this.filteredItems[this.selectedIndex];
            cb(args);
        }
    }

    /**
     * show list
     */
    show() {
        if (!this.list.hidden) return;
        this.callCallback("Open");
        const dialog = this.searchInput.parentNode.host.closest("dialog");
        dialog ? dialog.appendChild(this.list) : document.body.appendChild(this.list);
        this.updatePosition();
        this.update();
    }

    /**
     * hide list
     */
    hide() {
        this.searchInput?.blur();
        if (!this.list || this.list.hidden) return;
        this.callCallback("Close");
        this.list.remove();
        this.resetPosition();
    }

    /**
     * select next item
     */
    next() {
        if (this.itemIndex < this.filteredItems.length - 1) {
            this.itemIndex++;
        } else {
            this.itemIndex = 0;
        }
    }

    /**
     * select previous item
     */
    prev() {
        if (this.itemIndex > 0) {
            this.itemIndex--;
        } else {
            this.itemIndex = this.filteredItems.length - 1;
        }
    }

    /**
     * select item and hide list
     */
    selectAfter() {
        this.callCallback("Change");
        this.hide();
    }

    /**
     * deselect option (multiselect)
     * @param {Number} idx
     */
    deselectOption(idx) {
        if (!this.multiselect) return;
        this.selectedIndex = this.buffer.items.findIndex((i) => i.idx === idx);
        this.callCallback("Change");
    }

    resetPosition() {
        this.listConstraints = {
            top: "-200vh",
            left: "-200vw",
            bottom: "",
            minWidth: "",
            maxHeight: ""
        };
    }

    /**
     * update list position
     * @returns {OptionsHandler}
     */
    updatePosition() {
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const rect = entry.boundingClientRect;
                const spaceBelow = window.innerHeight - rect.bottom;
                const isBottom = rect.top < spaceBelow || spaceBelow >= OptionsHandler.listMaxHeight;
                this.listConstraints = {
                    top: isBottom ? `${rect.bottom}px` : "auto",
                    bottom: isBottom ? "auto" : `${window.innerHeight - rect.top}px`,
                    left: `${rect.left}px`,
                    minWidth: `${rect.width}px`,
                    maxHeight: `${Math.min(isBottom ? spaceBelow : rect.top, OptionsHandler.listMaxHeight)}px`
                };
            }

            observer.disconnect();
        });

        observer.observe(this.boundingBox);
        return this;
    }

    /**
     * update list items
     */
    update() {
        if (typeof this.hasInput === "undefined" && this.list.hidden) return;
        delete this.hasInput;
        const items = this.filteredItems;
        if (!this.searchable && items.length === 0) {
            this.hide();
            return;
        } else {
            this.show();
        }
        this.list.empty();
        items.forEach(this.createOption);
    }

    /**
     * create option
     * @param {option} item
     * @param {Number} key
     */
    createOption(item, key) {
        const option = document.createElement("div");
        option.appendChild(document.createTextNode(item.label));
        option.classList.add(CLASSES.item);
        if (item.disabled) {
            option.dataset.disabled = "disabled";
        }
        if (item.value) {
            option.dataset.value = item.value;
        }
        option.dataset.index = item.idx.toString();
        if (this.multiselect) {
            if (this.buffer.selectedOptions.includes(key)) {
                option.classList.add(CLASSES.selected);
            }
        } else {
            if (item.value === this.value?.value) {
                option.classList.add(CLASSES.selected);
            }
        }
        this.list.addOption(option);
    }

    addItem(item) {
        this.buffer.items.push(item);
    }

    removeItem(item) {
        this.buffer.items.splice(
            this.buffer.items.findIndex((i) => i.label === item.label && i.value === item.value),
            1
        );
    }

    /**
     * handle input focus
     */
    focusHandler() {
        this.callCallback("Focus");
        this.show();
    }

    /**
     * handle input blur
     */
    blurHandler() {
        if (!this.searchable && this.selectedIndex < 0) {
            this.searchInput.value = "";
        }
        this.callCallback("Blur");
        this.hide();
    }

    /**
     * handle key down event
     * @param e
     */
    keydownHandler(e) {
        this.show();
        if (e.code === KEYCODES.down) {
            e.preventDefault();
            this.next();
        } else if (e.code === KEYCODES.up) {
            e.preventDefault();
            this.prev();
        } else if (e.code === KEYCODES.enter) {
            e.preventDefault();
            e.stopPropagation();
        } else if (e.code === KEYCODES.tab) {
            e.preventDefault();
            this.blurHandler();
        }
    }

    /**
     * handle key up event
     * @param e
     */
    keyupHandler(e) {
        if (e.code === KEYCODES.enter) {
            e.preventDefault();
            if (!this.list.hidden) {
                const visibleItems = this.filteredItems;
                const idx = visibleItems[this.itemIndex].idx;
                const item = this.items.find((i) => i.idx === idx);
                if (!item.disabled) {
                    this.selectedIndex = item.idx;
                    this.selectAfter();
                }
            }
        }
        if (e.code === KEYCODES.escape) {
            this.searchInput.blur();
        }
    }

    /**
     * handle input event
     * @param {PointerEvent} e
     */
    inputHandler() {
        this.hasInput = true;
        if (!this.filteredExternal) {
            this.update();
        }
    }

    listScrollHandler() {
        if ("undefined" !== typeof this.listScrollTimeout) {
            clearTimeout(this.listScrollTimeout);
        }
        this.listScrollTimeout = setTimeout(() => {
            this.searchInput.removeEventListener("blur", this.handleBlur);
            this.searchInput.addEventListener("blur", this.handleBlur);
        }, 300);
    }

    /**
     * handle list pointer down event
     * removes blur listener to prevent list from disappearing before pointerup event
     */
    listPointerDownHandler(e) {
        const item = this.items.find((item) => item.idx === parseInt(e.target.dataset.index));
        if (item?.disabled) {
            e.preventDefault();
        }
        this.searchInput.removeEventListener("blur", this.handleBlur);
    }

    /**
     * handle list click
     * @param {PointerEvent} e
     */
    listPointerUpHandler(e) {
        e.stopImmediatePropagation();
        const item = this.items.find((item) => item.idx === parseInt(e.target.dataset.index));
        if (item && !item.disabled) {
            this.selectedIndex = item.idx;
            setTimeout(() => this.selectAfter(), 50);
        }
        this.searchInput.addEventListener("blur", this.handleBlur);
    }

    /**
     * handle resize and orientationchange events
     */
    repositionHandler() {
        if (this.repositionTimeout) clearTimeout(this.repositionTimeout);
        this.repositionTimeout = setTimeout(() => {
            this.updatePosition();
            delete this.repositionTimeout;
        }, 300);
    }

    disableOption(v, d) {
        this.items.find((i) => i.value === v).disabled = d;
    }

    /**
     * smash 'em
     */
    destroy() {
        this.removeListeners();
        delete this.cbChange;
        delete this.cbBlur;
        delete this.cbFocus;
        delete this.cbOpen;
        delete this.cbClose;
        delete this.list;
        delete this.searchInput;
    }

    /**
     * set list constraints
     * @param {listConstraints} constraints
     */
    set listConstraints(constraints) {
        for (let key of Object.keys(constraints)) {
            const prop = key.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
            this.list?.style.setProperty(`--${prop}`, constraints[key]);
        }
    }

    /**
     * @returns {listConstraints}
     */
    get listConstraints() {
        const listStyles = getComputedStyle(this.list);
        return {
            top: listStyles.getPropertyValue("top"),
            left: listStyles.getPropertyValue("left"),
            bottom: listStyles.getPropertyValue("bottom"),
            maxHeight: listStyles.getPropertyValue("max-height"),
            minWidth: listStyles.getPropertyValue("min-width")
        };
    }

    /**
     * set flag filtered external
     * @param {Boolean} f
     */
    set filteredExternal(f) {
        if (f) {
            this.searchInput.dataset.externelFilter = "true";
        } else {
            delete this.searchInput.dataset.externelFilter;
        }
    }

    /**
     * obtain flag filtered external
     * @returns {boolean}
     */
    get filteredExternal() {
        return typeof this.searchInput.dataset.externelFilter !== "undefined";
    }

    /**
     * set items
     * @param {Array.<option>} i
     */
    set items(i) {
        const canUpdate = !!this.buffer.items;
        this.buffer.items = i;
        if (canUpdate) {
            this.update();
        }
    }

    /**
     * obtain items
     * @returns {Array.<option>}
     */
    get items() {
        const items = [...this.buffer.items];
        items.forEach((v, k) => {
            v.idx = k;
        });
        return items;
    }

    /**
     * obtain items filtered by input
     * @returns {Array.<option>}
     */
    get filteredItems() {
        if (this.searchable && !this.filteredExternal) {
            const search = this.searchInput.value.toLowerCase();
            return this.items.filter((i) => i.label.toLowerCase().includes(search));
        }
        return this.items;
    }

    /**
     * set index of current selected item
     * @param {Number} idx
     */
    set selectedIndex(idx) {
        if (Number.isNaN(idx)) return;
        this.buffer.selectedIndex = this.items.findIndex((i) => i.idx === idx);
        const item = this.items.find((i) => i.idx === idx);
        if (this.multiselect) {
            this.searchInput.value = this.searchInput.placeholder;
            const option = this.buffer.selectedOptions.indexOf(this.buffer.selectedIndex);
            if (option > -1) {
                this.buffer.selectedOptions.splice(option, 1);
            } else {
                this.buffer.selectedOptions.push(this.buffer.selectedIndex);
            }
            if (this.buffer.selectedOptions.length === 0) {
                this.searchInput.value = "";
            }
        } else {
            this.searchInput.value = item?.label || "";
        }
    }

    /**
     * obtain current selected index
     * @returns {Number}
     */
    get selectedIndex() {
        return this.buffer.selectedIndex;
    }

    set itemIndex(idx) {
        if (Number.isNaN(idx)) return;
        this.buffer.itemIndex = idx;
        if (!this.searchable) {
            this.searchInput.value = this.filteredItems[idx]?.label ?? "";
        }
        if (this.list && idx >= 0 && idx < this.filteredItems.length && this.list.childNodes.length > 0) {
            this.list.container.childNodes.forEach((n) => n.classList.remove(CLASSES.selected));
            this.list.container.childNodes[idx].classList.add(CLASSES.selected);
            this.list.container.childNodes[idx].scrollIntoView();
        }
    }

    get itemIndex() {
        return this.buffer.itemIndex;
    }

    /**
     * set current value
     * @param {option} v
     */
    set value(v) {
        this.selectedIndex = Math.max(
            0,
            this.items.findIndex((i) => i.value === v?.value)
        );
        this.callCallback("Change");
    }

    /**
     * obtain current value
     * @returns {option}
     */
    get value() {
        return this.items[this.selectedIndex];
    }

    get selectedOptions() {
        return this.buffer.selectedOptions.map((idx) => this.items[idx]);
    }

    /**
     * @param {option[]} options
     */
    set selectedOptions(options) {
        if (this.multiselect) {
            options.forEach((o) => {
                const index = this.items.findIndex((i) => i.value === o.value);
                if (index > -1) {
                    this.selectedIndex = index;
                    this.selectAfter();
                }
            });
        }
    }
}
